import { createTheme } from '@mui/material/styles';

export const THEME_MODE_STORAGE_KEY = 'themeMode';

const lightPalette = {
  mode: 'light',
  chart: {
    successfulResults: '#4f9b7bff',
    unsuccessfulResults: '#d32f2f',
  },
  primary: {
    main: '#4f9b7bff',
    white: '#FFFFFF',
    black: '#000000',
    gray: '#fafbfc',
  },
  secondary: {
    main: '#000000',
  },
  logo: {
    main: '#4f9b7bff',
  },
  background: {
    default: '#F5F5F5',
    paper: '#FFFFFF',
  },
  link: {
    main: '#4f9b7bff',
    menu: '#000',
  },
  action: {
    hover: '#ebebeb',
    hoverDark: '#000',
    lightHover: '#f7f7f7f3',
  },
  menu: {
    selected: '#ecf4f0',
    selectedSpan: '#4f9b7bff',
  },
  custom: {
    darkGreen: '#18221d',
    lightGreen: '#5f6f66',
    whiteGreen: '#dfe8e3',
    shadow: '#abb4b0',
  },
  notification: {
    space: '#7e57c2',
    inspection: '#1976d2',
    dueSoon: '#f9a825',
    overdue: '#d32f2f',
    limitReached: '#ef6c00',
    unreadDot: '#4f9b7bff',
    unreadBackground: '#f0f7f3',
  },
};

const darkPalette = {
  mode: 'dark',
  chart: {
    successfulResults: '#6fbfa0',
    unsuccessfulResults: '#ef5350',
  },
  primary: {
    main: '#6fbfa0',
    white: '#1e1e1e',
    black: '#FFFFFF',
    gray: '#2a2a2a',
  },
  secondary: {
    main: '#FFFFFF',
  },
  logo: {
    main: '#6fbfa0',
  },
  background: {
    default: '#121212',
    paper: '#1e1e1e',
  },
  link: {
    main: '#6fbfa0',
    menu: '#FFFFFF',
  },
  action: {
    hover: '#2a2a2a',
    hoverDark: '#FFFFFF',
    lightHover: '#1a1a1a',
  },
  menu: {
    selected: '#2a3a33',
    selectedSpan: '#6fbfa0',
  },
  custom: {
    darkGreen: '#dfe8e3',
    lightGreen: '#a8b8b0',
    whiteGreen: '#2a3a33',
    shadow: '#000000',
  },
  notification: {
    space: '#b39ddb',
    inspection: '#64b5f6',
    dueSoon: '#ffd54f',
    overdue: '#ef5350',
    limitReached: '#ffa726',
    unreadDot: '#6fbfa0',
    unreadBackground: '#1f2a25',
  },
};

export const createAppTheme = (mode) => {
  const isDark = mode === 'dark';
  const inputBackground = isDark ? '#2a2a2a' : '#FFFFFF';

  return createTheme({
    typography: {
      fontFamily: ['Noto Sans', 'Arial', 'sans-serif'].join(','),
      fontSize: 14,
      fontSizeMedium: 16,
      fontSizeBig: 26,
      fontSizeVeryBig: 40,
      fontSizeSmall: 12,
      lineHeight: 1.6,
    },
    palette: isDark ? darkPalette : lightPalette,
    padding: {
      small: '5px',
      content: '10px',
      container: '70px',
      containerBig: '50px',
      section: '20px',
      footer: '20px',
      sideMenuButton: '65px',
      sideMenuButtonHidden: '30px',
    },
    radius: {
      card: '6px',
    },
    height: {
      topMenu: '60px',
      footer: '100px',
      notificationDropdown: '480px',
    },
    width: {
      sideMenu: '300px',
      sideMenuLimited: '160px',
      filterDrawer: '450px',
      form: '500px',
      formMin: '300px',
      topMenuLogo: '40px',
      descriptionItem: '300px',
      limitedSpan: '200px',
      notificationDropdown: '380px',
    },
    shape: {
      borderRadius: 8, // Default border radius for all components
    },
    components: {
      MuiContainer: {
        styleOverrides: {
          root: {
            '&.MuiContainer-maxWidthXl': {
              maxWidth: '1450px',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {},
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
            '&:hover': {
              backgroundColor: inputBackground,
            },
            '&.Mui-focused': {
              backgroundColor: inputBackground,
            },
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: inputBackground,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            ...(isDark && { color: darkPalette.secondary.main }),
          },
        },
      },
    },
  });
};

const getStoredThemeMode = () => {
  const stored = localStorage.getItem(THEME_MODE_STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
};

const theme = createAppTheme(getStoredThemeMode());

export default theme;
